/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
  Box, IconButton, Popover, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel, Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Swal from 'sweetalert2';
import MDButton from 'components/MDButton';
import { api } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { DROPDOWN_SX, getCurrentUserId } from '../opdAppointments/constants';

// Same "pill" visual language as AppointmentDetailDrawer's ActionIcon, but
// icon-only (the popover already labels itself via the tooltip) so several
// fit comfortably in the quick-action row.
function QuickActionIcon({ title, icon, color, onClick, disabled }) {
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onClick}
          sx={{
            bgcolor: disabled ? '#eceff1' : color,
            color: '#fff',
            boxShadow: disabled ? 'none' : '0 2px 6px rgba(0,0,0,0.18)',
            '&:hover': { bgcolor: disabled ? '#eceff1' : color, filter: disabled ? 'none' : 'brightness(0.9)' },
            '&.Mui-disabled': { color: '#b0bec5' },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}

/**
 * "⋮" quick-action trigger for an OPD bill — Cancel & Regenerate / Refund
 * (and optionally View), usable from any row/summary that only has a bill
 * id or a summary bill object, not just from the full BillDetail screen.
 * Always re-fetches the full bill (payments/refunds) when opened so actions
 * act on current data even if the caller's row is stale.
 *
 * Props:
 *   bill    - optional summary bill object (must have at least `.id`)
 *   billId  - optional bill id, used if `bill` isn't provided
 *   onSuccess - called after a cancel/refund completes, to let the caller refresh
 *   onView  - optional; if provided, an extra "View Bill" quick action is shown
 */
export default function BillQuickActionsMenu({ bill: billProp, billId, onSuccess, onView }) {
  const { hasPermission } = usePermissions();
  const [anchorEl, setAnchorEl] = useState(null);
  const [bill, setBill] = useState(billProp || null);
  const [loadingBill, setLoadingBill] = useState(false);
  const [dialog, setDialog] = useState(null); // 'cancel' | 'refund' | null

  const [cancelForm, setCancelForm] = useState({ reason: '', refund_method: 'CASH' });
  const [cancelling, setCancelling] = useState(false);

  const [refundForm, setRefundForm] = useState({ refund_type: 'FULL', amount: '', refund_method: 'CASH', reason: '' });
  const [refunding, setRefunding] = useState(false);

  const effectiveId = billProp?.id ?? billId;

  const openMenu = async (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    if (!effectiveId) return;
    setLoadingBill(true);
    try {
      const resp = await api.opdBilling.getBillById(effectiveId);
      setBill(resp?.data || billProp || null);
    } catch {
      setBill(billProp || null);
    } finally {
      setLoadingBill(false);
    }
  };
  const closeMenu = () => setAnchorEl(null);
  const closeDialog = () => setDialog(null);

  const refundedTotal = (bill?.refunds || []).reduce((s, r) => s + Number(r.amount), 0);
  const refundableBalance = bill ? Math.max(Number(bill.paid_amount) - refundedTotal, 0) : 0;

  const openCancelDialog = () => {
    setCancelForm({ reason: '', refund_method: 'CASH' });
    setDialog('cancel');
    closeMenu();
  };
  const openRefundDialog = () => {
    const canPartial = hasPermission('v1-opd-billing-refund-partial');
    setRefundForm({
      refund_type: canPartial ? 'PARTIAL' : 'FULL',
      amount: canPartial ? '' : String(refundableBalance),
      refund_method: 'CASH',
      reason: '',
    });
    setDialog('refund');
    closeMenu();
  };

  const handleCancelAndRegenerate = async () => {
    if (!cancelForm.reason.trim()) {
      Swal.fire({ icon: 'warning', title: 'Enter a cancellation reason' });
      return;
    }
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Cancel this bill?',
      text: 'The paid amount will be reversed on the ledger and a fresh bill generated for this appointment.',
      showCancelButton: true,
      confirmButtonText: 'Cancel & Regenerate',
    });
    if (!confirm.isConfirmed) return;

    setCancelling(true);
    try {
      await api.opdBilling.cancelAndRegenerate(bill.id, {
        reason: cancelForm.reason,
        refund_method: Number(bill.paid_amount) > 0 ? cancelForm.refund_method : undefined,
        cancelled_by: getCurrentUserId(),
      });
      setDialog(null);
      Swal.fire({ icon: 'success', title: 'Bill cancelled, new bill generated', timer: 1800, showConfirmButton: false });
      onSuccess?.();
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setCancelling(false);
    }
  };

  const handleRefund = async () => {
    const payload = {
      refund_method: refundForm.refund_method,
      reason: refundForm.reason || undefined,
      refunded_by: getCurrentUserId(),
    };

    setRefunding(true);
    try {
      if (refundForm.refund_type === 'PARTIAL') {
        const amount = parseFloat(refundForm.amount);
        if (!amount || amount <= 0) {
          Swal.fire({ icon: 'warning', title: 'Enter a valid refund amount' });
          setRefunding(false);
          return;
        }
        if (amount > refundableBalance) {
          Swal.fire({ icon: 'warning', title: `Amount exceeds refundable balance of ₹${refundableBalance.toFixed(2)}` });
          setRefunding(false);
          return;
        }
        await api.opdBilling.refundPartial(bill.id, { ...payload, amount });
      } else {
        await api.opdBilling.refundFull(bill.id, payload);
      }
      setDialog(null);
      Swal.fire({ icon: 'success', title: 'Refund processed', timer: 1500, showConfirmButton: false });
      onSuccess?.();
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setRefunding(false);
    }
  };

  const canCancel = hasPermission('v1-opd-billing-cancel-regenerate');
  const canRefund = hasPermission('v1-opd-billing-refund-full');
  const isCancelled = bill?.status === 'CANCELLED';

  return (
    <>
      <IconButton size="small" onClick={openMenu} sx={{ color: '#78909c' }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1, display: 'flex', gap: 0.75 }} onClick={e => e.stopPropagation()}>
          {loadingBill ? (
            <Box sx={{ px: 1.5, py: 0.5 }}>
              <Typography variant="caption" color="textSecondary">Loading…</Typography>
            </Box>
          ) : (
            <>
              {onView && (
                <QuickActionIcon title="View Bill" icon={<VisibilityIcon fontSize="small" />} color="#546e7a" onClick={() => { closeMenu(); onView(bill); }} />
              )}
              {canCancel && (
                <QuickActionIcon
                  title={isCancelled ? 'Already cancelled' : 'Cancel & Regenerate'}
                  icon={<CancelIcon fontSize="small" />}
                  color="#c62828"
                  disabled={isCancelled}
                  onClick={openCancelDialog}
                />
              )}
              {canRefund && (
                <QuickActionIcon
                  title={isCancelled ? 'Already cancelled' : refundableBalance <= 0 ? 'Nothing to refund' : 'Refund'}
                  icon={<UndoIcon fontSize="small" />}
                  color="#ef6c00"
                  disabled={isCancelled || refundableBalance <= 0}
                  onClick={openRefundDialog}
                />
              )}
            </>
          )}
        </Box>
      </Popover>

      {/* Cancel & Regenerate */}
      <Dialog open={dialog === 'cancel'} onClose={closeDialog} maxWidth="xs" fullWidth onClick={e => e.stopPropagation()}>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Cancel &amp; Regenerate Bill</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          <Typography variant="caption" color="textSecondary">
            {bill?.bill_no || (bill ? `#${bill.id}` : '')} — a fresh bill will be generated for this appointment.
          </Typography>
          <TextField
            label="Cancellation Reason"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={cancelForm.reason}
            onChange={e => setCancelForm(f => ({ ...f, reason: e.target.value }))}
          />
          {Number(bill?.paid_amount) > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel id="qa-cancel-refund-method-label">Refund Method</InputLabel>
              <Select
                labelId="qa-cancel-refund-method-label"
                label="Refund Method"
                value={cancelForm.refund_method}
                onChange={e => setCancelForm(f => ({ ...f, refund_method: e.target.value }))}
                sx={DROPDOWN_SX}
              >
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="ONLINE">Online</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={closeDialog} sx={{ textTransform: 'none' }}>Close</Button>
          <MDButton size="small" variant="gradient" color="error" disabled={cancelling} onClick={handleCancelAndRegenerate}>
            {cancelling ? 'Cancelling…' : 'Confirm Cancel & Regenerate'}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Refund */}
      <Dialog open={dialog === 'refund'} onClose={closeDialog} maxWidth="xs" fullWidth onClick={e => e.stopPropagation()}>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Refund</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          <Typography variant="caption" color="textSecondary">
            {bill?.bill_no || (bill ? `#${bill.id}` : '')} — refundable balance: ₹{refundableBalance.toFixed(2)}
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel id="qa-refund-type-label">Refund Type</InputLabel>
            <Select
              labelId="qa-refund-type-label"
              label="Refund Type"
              value={refundForm.refund_type}
              onChange={e => {
                const refund_type = e.target.value;
                setRefundForm(f => ({ ...f, refund_type, amount: refund_type === 'FULL' ? String(refundableBalance) : '' }));
              }}
              sx={DROPDOWN_SX}
            >
              <MenuItem value="FULL">Full</MenuItem>
              {hasPermission('v1-opd-billing-refund-partial') && <MenuItem value="PARTIAL">Partial</MenuItem>}
            </Select>
          </FormControl>
          {refundForm.refund_type === 'PARTIAL' && (
            <TextField
              label="Refund Amount"
              size="small"
              type="number"
              fullWidth
              value={refundForm.amount}
              onChange={e => setRefundForm(f => ({ ...f, amount: e.target.value }))}
            />
          )}
          <FormControl size="small" fullWidth>
            <InputLabel id="qa-refund-method-label">Refund Method</InputLabel>
            <Select
              labelId="qa-refund-method-label"
              label="Refund Method"
              value={refundForm.refund_method}
              onChange={e => setRefundForm(f => ({ ...f, refund_method: e.target.value }))}
              sx={DROPDOWN_SX}
            >
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="ONLINE">Online</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Reason (optional)"
            size="small"
            fullWidth
            value={refundForm.reason}
            onChange={e => setRefundForm(f => ({ ...f, reason: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={closeDialog} sx={{ textTransform: 'none' }}>Close</Button>
          <MDButton size="small" variant="gradient" color="warning" disabled={refunding} onClick={handleRefund}>
            {refunding ? 'Processing…' : 'Confirm Refund'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
